import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { Delete, Edit, Email } from '@mui/icons-material'
import {
  Box,
  Button,
  Chip,
  ChipProps,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import {
  AccessInvitation,
  Mutation,
  Query,
} from '@promoboxx/graphql-gateway-types'
import I18n from 'i18n-js'
import { enqueueSnackbar } from 'notistack'
import { useMemo } from 'react'

import { invalidateApolloQuery } from '@src/config/ApolloClient'
import { useSimpleConfirmDialog } from '@src/lib/SimpleConfirmDialog'
import useCurrentAuth from '@src/lib/useCurrentAuth'
import useCurrentRetailerAccount from '@src/lib/useCurrentRetailerAccount'
import useIsCurrentUserInternal from '@src/lib/useIsCurrentUserInternal'
import useModalState from '@src/lib/useModalState'

import InviteUsersDialog from './InviteUsersDialog'

const RetailerSettingsUsers: React.FC = () => {
  const { ensureCurrentRetailerAccount } = useCurrentRetailerAccount()
  const { ensureCurrentUser } = useCurrentAuth()
  const isInternal = useIsCurrentUserInternal()

  const { data, loading } = useQuery<Query>(QUERY, {
    variables: {
      retailer_account_id: ensureCurrentRetailerAccount.id,
      retailer_ids: ensureCurrentRetailerAccount.retailers?.map((x) => x.id),
    },
  })

  const [deletePermission] = useMutation<Mutation>(DELETE_PERMISSION)
  const [deleteAccessInvitation] = useMutation<Mutation>(
    DELETE_ACCESS_INVITATION,
  )
  const [resendAccessInvitation] = useMutation<Mutation>(
    RESEND_ACCESS_INVITATION,
  )
  const [getPermissionsForRetailerAccount] = useLazyQuery<Query>(
    GET_PERMISSIONS,
    {
      fetchPolicy: 'network-only',
    },
  )

  const doSomeBrandsLockUserCreation = Boolean(
    ensureCurrentRetailerAccount.retailers?.some(
      (x) => x.brand?.lock_user_creation,
    ),
  )
  const canInviteUsers = isInternal || !doSomeBrandsLockUserCreation

  const { confirm } = useSimpleConfirmDialog()

  const inviteDialog = useModalState()

  const accessInvitations = useMemo(() => {
    const accessInvitations: AccessInvitation[] = [
      ...(data?.getAccessInvitations?.data || []),
      ...(data?.getAccessInvitationsByRetailerIds?.data || []),
    ].filter((x) => x.state === 'open')

    accessInvitations.sort((a, b) => {
      const nameA = `${a.recipient?.first_name} ${a.recipient?.last_name}`
      const nameB = `${b.recipient?.first_name} ${b.recipient?.last_name}`

      return nameA.localeCompare(nameB)
    })

    return accessInvitations
  }, [
    data?.getAccessInvitations?.data,
    data?.getAccessInvitationsByRetailerIds?.data,
  ])

  return (
    <>
      <Stack direction="column" spacing={4} sx={{ marginTop: 5 }}>
        <Stack direction="row">
          <Typography variant="h6" gutterBottom>
            {I18n.t('retailerSettings.users.title')}
          </Typography>

          <Box flexGrow={1} />

          <Button
            variant="contained"
            onClick={inviteDialog.openModal}
            disabled={!canInviteUsers}
          >
            {I18n.t('retailerSettings.users.inviteUsers.title')}
          </Button>
        </Stack>

        <Paper>
          <List disablePadding>
            {loading ? (
              <>
                <UserListItem fullName="" email="" type="" loading />
                <UserListItem fullName="" email="" type="" loading />
                <UserListItem fullName="" email="" type="" loading />
                <UserListItem fullName="" email="" type="" loading />
                <UserListItem fullName="" email="" type="" loading />
              </>
            ) : undefined}

            {data?.getUsers?.map((x) => {
              const fullName = `${x.first_name} ${x.last_name}`
              const isCurrentUser = x.id === ensureCurrentUser.id

              return (
                <UserListItem
                  key={x.id}
                  fullName={fullName}
                  email={x.email || ''}
                  type={x.type || ''}
                  editHref={isCurrentUser ? '/#/account/profile' : undefined}
                  onDeleteClick={
                    isCurrentUser
                      ? undefined
                      : async () => {
                          if (await confirm({})) {
                            try {
                              const { data } =
                                await getPermissionsForRetailerAccount({
                                  variables: {
                                    id: ensureCurrentRetailerAccount.id,
                                  },
                                })

                              const permissions = data?.getPermissions || []
                              let found = permissions.find((permission) => {
                                return (
                                  permission.user_id === x.id &&
                                  permission.permissionable_type ===
                                    'RetailerAccount' &&
                                  permission.permissionable_id ===
                                    ensureCurrentRetailerAccount.id
                                )
                              })

                              if (!found) {
                                throw new Error(
                                  `Could not find permission for ${fullName}`,
                                )
                              }

                              await deletePermission({
                                variables: {
                                  id: found.id,
                                },
                              })

                              enqueueSnackbar(
                                I18n.t(
                                  'retailerSettings.users.notifications.deleteUserSuccess',
                                  {
                                    fullName,
                                  },
                                ),
                                {
                                  variant: 'success',
                                },
                              )

                              invalidateApolloQuery(['getUsers'])
                            } catch (err) {
                              console.error(err)

                              enqueueSnackbar(
                                I18n.t(
                                  'retailerSettings.users.notifications.deleteUserError',
                                  { fullName },
                                ),
                                {
                                  variant: 'error',
                                },
                              )
                            }
                          }
                        }
                  }
                />
              )
            })}
          </List>
        </Paper>

        <Typography variant="h6" gutterBottom>
          {I18n.t('retailerSettings.users.pendingInvitations')}
        </Typography>

        <Paper>
          <List disablePadding>
            {loading ? (
              <>
                <UserListItem fullName="" email="" type="" loading />
                <UserListItem fullName="" email="" type="" loading />
              </>
            ) : undefined}

            {accessInvitations.map((x) => {
              return (
                <UserListItem
                  key={x.id}
                  fullName={`${x.recipient?.first_name || ''} ${
                    x.recipient?.last_name || ''
                  }`}
                  email={x.recipient?.email || ''}
                  type={x.state || ''}
                  onDeleteClick={async () => {
                    try {
                      if (await confirm({})) {
                        await deleteAccessInvitation({
                          variables: { id: x.id },
                        })

                        invalidateApolloQuery([
                          'getAccessInvitations',
                          'getAccessInvitationsByRetailerIds',
                        ])

                        enqueueSnackbar(
                          I18n.t(
                            'retailerSettings.users.notifications.deleteAccessInvitationSuccess',
                          ),
                          {
                            variant: 'success',
                          },
                        )
                      }
                    } catch (err) {
                      console.error(err)

                      enqueueSnackbar(
                        I18n.t(
                          'retailerSettings.users.notifications.deleteAccessInvitationError',
                        ),
                        {
                          variant: 'error',
                        },
                      )
                    }
                  }}
                  onMessageClick={async () => {
                    try {
                      await resendAccessInvitation({
                        variables: { id: x.id },
                      })

                      enqueueSnackbar(
                        I18n.t(
                          'retailerSettings.users.notifications.resendAccessInvitationSuccess',
                        ),
                        {
                          variant: 'success',
                        },
                      )
                    } catch (err) {
                      console.error(err)

                      enqueueSnackbar(
                        I18n.t(
                          'retailerSettings.users.notifications.resendAccessInvitationError',
                        ),
                        {
                          variant: 'error',
                        },
                      )
                    }
                  }}
                />
              )
            })}
          </List>
        </Paper>
      </Stack>

      <InviteUsersDialog
        open={inviteDialog.isOpen}
        onClose={inviteDialog.closeModal}
        invitationSenderUserId={ensureCurrentUser.id || ''}
        invitationName={`Invitation to manage ${ensureCurrentRetailerAccount.name}`}
        invitationAccessibleType="RetailerAccount"
        invitationAccessibleId={ensureCurrentRetailerAccount.id}
      />
    </>
  )
}

const UserListItem: React.FC<{
  fullName: string
  email: string
  type: string
  editHref?: string
  onDeleteClick?: () => void
  onMessageClick?: () => void
  loading?: boolean
}> = (props) => {
  return (
    <>
      <ListItem divider>
        <ListItemText
          primary={props.loading ? <Skeleton width={100} /> : props.fullName}
          secondary={props.loading ? <Skeleton width={75} /> : props.email}
          secondaryTypographyProps={{ noWrap: true }}
        />

        {props.loading ? (
          <Skeleton height={24} width={80} />
        ) : (
          <Chip
            label={I18n.t(
              `retailerSettings.users.userOrAccessInvitationState.${props.type}`,
              { default: props.type },
            )}
            size="small"
            color={STATUS_TO_CHIP_COLOR[props.type]}
          />
        )}

        <Box
          sx={{
            // 60px is the width of two small IconButtons.
            flex: '0 0 60px',
            textAlign: 'right',
          }}
        >
          {!props.loading && props.onMessageClick ? (
            <IconButton size="small" edge="end" onClick={props.onMessageClick}>
              <Email fontSize="small" />
            </IconButton>
          ) : undefined}

          {!props.loading && props.editHref ? (
            <IconButton size="small" edge="end" href={props.editHref}>
              <Edit fontSize="small" />
            </IconButton>
          ) : undefined}

          {!props.loading && props.onDeleteClick ? (
            <IconButton size="small" onClick={props.onDeleteClick}>
              <Delete fontSize="small" />
            </IconButton>
          ) : undefined}
        </Box>
      </ListItem>
    </>
  )
}

const QUERY = gql`
  query getUsersRetailerSettingsUsers(
    $retailer_account_id: ID!
    $retailer_ids: [ID!]!
  ) {
    getUsers(
      resource_type: "retailer_accounts"
      resource_id: $retailer_account_id
    ) {
      id
      first_name
      last_name
      email
      type
    }

    getAccessInvitations(
      resourceType: retailer_accounts
      resourceId: $retailer_account_id
      params: { fields: ["recipient"] }
    ) {
      data {
        id
        state
        recipient {
          id
          first_name
          last_name
          email
        }
      }
    }

    getAccessInvitationsByRetailerIds(
      retailer_ids: $retailer_ids
      params: { fields: ["recipient"] }
    ) {
      data {
        id
        state
        recipient {
          id
          first_name
          last_name
          email
        }
      }
    }
  }
`

const DELETE_PERMISSION = gql`
  mutation deleteUserRetailerSettingsUsers($id: ID!) {
    deletePermission(id: $id)
  }
`

const GET_PERMISSIONS = gql`
  query getPermissionsRetailerSettingsUsers($id: ID!) {
    getPermissions(resource_type: "retailer_accounts", resource_id: $id) {
      id
      permissionable_type
      permissionable_id
      action
      user_id
    }
  }
`

const DELETE_ACCESS_INVITATION = gql`
  mutation deleteAccessInvitationRetailerSettingsUsers($id: ID!) {
    deleteAccessInvitation(id: $id)
  }
`

const RESEND_ACCESS_INVITATION = gql`
  mutation resendAccessInvitationRetailerSettingsUsers($id: ID!) {
    resendAccessInvitation(id: $id)
  }
`

const STATUS_TO_CHIP_COLOR: Record<string, ChipProps['color']> = {
  ActiveUser: 'success',
  InactiveUser: 'warning',
  open: 'info',
}

export default RetailerSettingsUsers
